import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { 
  Globe, 
  Bell,
  Menu, 
  User, 
  LogOut, 
  LayoutDashboard,
  ChevronDown,
  Ship,
  Package,
  Users,
  FileText,
  Newspaper,
  Info,
  Phone
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://api.afrigulfmarket.com";

const Layout = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const scrollToTarget = () => {
      const { hash } = location;
      if (!hash) {
        window.scrollTo(0, 0);
        return;
      }
      const target = document.querySelector(hash);
      if (target) {
        const header = document.querySelector("header");
        const headerOffset = header ? header.getBoundingClientRect().height + 16 : 120;
        const elementTop = target.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = Math.max(elementTop - headerOffset, 0);
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    };

    requestAnimationFrame(scrollToTarget);
  }, [location]);

  const languages = [
    { code: 'en', name: t('language.en') },
    { code: 'ar', name: t('language.ar') },
    { code: 'ur', name: t('language.ur') },
    { code: 'sw', name: t('language.sw') },
  ];

  const navLinks = [
    { path: '/marketplace', label: t('nav.marketplace'), icon: Package },
    { path: '/suppliers', label: t('nav.suppliers'), icon: Users },
    { path: '/rfq', label: t('nav.rfq'), icon: FileText },
    { path: '/logistics', label: t('nav.logistics'), icon: Ship },
    { path: '/blog', label: t('nav.blog'), icon: Newspaper },
    { path: '/about', label: t('nav.about'), icon: Info },
    { path: '/contact', label: t('nav.contact'), icon: Phone },
  ];

  useEffect(() => {
    if (user) {
      fetchNotifications();
    } else {
      setNotifications([]);
    }
  }, [user]);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

  const isActive = (path) => location.pathname === path;

  const isNotificationRead = (notification) => {
    return Boolean(notification.is_read ?? notification.read ?? notification.isRead);
  };

  const unreadCount = notifications.filter((notification) => !isNotificationRead(notification)).length;

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications/my`);
      setNotifications(response.data || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notificationId}/read`);
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, is_read: true, read: true, isRead: true }
            : notification
        )
      );
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const handleOpenNotification = (notification) => {
    if (!isNotificationRead(notification)) {
      handleMarkRead(notification.id);
    }
    const link =
      notification.link ||
      notification.url ||
      notification.path ||
      notification.href;
    if (link) {
      if (typeof link === 'string' && /^(https?:)?\/\//i.test(link)) {
        window.open(link, '_blank', 'noopener,noreferrer');
      } else {
        navigate(link);
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="container-main">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-10 h-10 bg-[#0D4F8B] rounded-sm flex items-center justify-center">
                <Ship className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-base sm:text-xl text-[#0D4F8B]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                AfriGulf Market
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.slice(0, 5).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.path.replace('/', '')}`}
                  className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-[#0D4F8B]/10 text-[#0D4F8B]'
                      : 'text-slate-600 hover:text-[#0D4F8B] hover:bg-slate-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-[#0D4F8B]">
                    More <ChevronDown className="ml-1 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {navLinks.slice(5).map((link) => (
                    <DropdownMenuItem key={link.path} asChild>
                      <Link to={link.path} className="flex items-center gap-2">
                        <link.icon className="h-4 w-4" />
                        {link.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {/* Right Section */}
            <div className="flex items-center gap-2">
              {user && (
                <DropdownMenu onOpenChange={(open) => { if (open) fetchNotifications(); }}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="relative h-9 w-9"
                      aria-label="Notifications"
                      data-testid="notifications-btn"
                    >
                      <Bell className="h-5 w-5" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
                          {unreadCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                    <div className="px-3 py-2 text-xs font-semibold text-slate-500">
                      Notifications
                    </div>
                    {notifications.length === 0 ? (
                      <div className="px-3 py-4 text-sm text-slate-500">
                        No notifications.
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <DropdownMenuItem
                          key={notification.id}
                          className={`flex flex-col items-start gap-1 ${
                            isNotificationRead(notification)
                              ? 'text-slate-500'
                              : 'bg-slate-50 text-slate-900'
                          }`}
                          onClick={() => handleOpenNotification(notification)}
                        >
                          <span className="text-sm font-medium">
                            {notification.title || 'Notification'}
                          </span>
                          {notification.body && (
                            <span className="text-xs text-slate-500">
                              {notification.body}
                            </span>
                          )}
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {/* Language Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    data-testid="language-switcher"
                    aria-label="Change language"
                  >
                    <Globe className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={i18n.language === lang.code ? 'bg-slate-100' : ''}
                      data-testid={`lang-${lang.code}`}
                    >
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Auth Buttons */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                      <div className="w-8 h-8 bg-[#0D4F8B] rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <span className="hidden md:block text-sm font-medium">{user.name}</span>
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-link">
                        <LayoutDashboard className="h-4 w-4" />
                        {t('nav.dashboard')}
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2" data-testid="admin-link">
                          <User className="h-4 w-4" />
                          {t('admin.title')}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-btn">
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="hidden sm:flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" data-testid="login-btn">{t('nav.login')}</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-[#0D4F8B] hover:bg-[#0A3D6B]" data-testid="register-btn">
                      {t('nav.register')}
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobile Menu */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" aria-label="Open menu">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80">
                  <nav className="flex flex-col gap-2 mt-8">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
                          isActive(link.path)
                            ? 'bg-[#0D4F8B]/10 text-[#0D4F8B]'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <link.icon className="h-5 w-5" />
                        {link.label}
                      </Link>
                    ))}
                    {!user && (
                      <div className="flex flex-col gap-2 mt-4 pt-4 border-t">
                        <Link to="/login" onClick={() => setMobileOpen(false)}>
                          <Button variant="outline" className="w-full">{t('nav.login')}</Button>
                        </Link>
                        <Link to="/register" onClick={() => setMobileOpen(false)}>
                          <Button className="w-full bg-[#0D4F8B] hover:bg-[#0A3D6B]">
                            {t('nav.register')}
                          </Button>
                        </Link>
                      </div>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>


      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-16">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-white rounded-sm flex items-center justify-center">
                  <Ship className="w-6 h-6 text-[#0D4F8B]" />
                </div>
                <span className="font-bold text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  AfriGulf Market
                </span>
              </div>
              <p className="text-slate-400 text-sm">
                {t('footer.tagline')}
              </p>
            </div>

            {/* Company Links */}
            <div>
              <h4 className="font-semibold mb-4 text-white">{t('footer.company')}</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/about" className="hover:text-white transition-colors">{t('nav.about')}</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">{t('nav.contact')}</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">{t('nav.blog')}</Link></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="font-semibold mb-4 text-white">{t('footer.resources')}</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/marketplace" className="hover:text-white transition-colors">{t('nav.marketplace')}</Link></li>
                <li><Link to="/suppliers" className="hover:text-white transition-colors">{t('nav.suppliers')}</Link></li>
                <li><Link to="/rfq" className="hover:text-white transition-colors">{t('nav.rfq')}</Link></li>
                <li><Link to="/logistics" className="hover:text-white transition-colors">{t('nav.logistics')}</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4 text-white">{t('footer.legal')}</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/privacy" className="hover:text-white transition-colors">{t('footer.privacy')}</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">{t('footer.terms')}</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;

